import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Search, FileText, User, FileSpreadsheet, Eye, Mail } from "lucide-react";
import { toast } from "sonner";
import { downloadApiFile } from "@/lib/downloadAuthedFile";
import { cacheGet, cachePut } from "@/lib/offline";
import { formatMoney, STATUS_META, formatDate } from "@/lib/format";
import ViewOnlyBanner from "@/components/ViewOnlyBanner";
import { useCanEdit } from "@/lib/permissions";
import { useAuth } from "@/context/AuthContext";

const STATUS_FILTERS = ["all", "draft", "sent", "accepted", "rejected"];

// Send/open tracking stamp — green once the client has opened the shared
// proposal link, blue after sending but before the first open.
function TrackingStamp({ q }) {
  if (q.last_viewed_at) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] text-emerald-400 whitespace-nowrap"
        data-testid={`quote-opened-stamp-${q.id}`}
      >
        <Eye className="h-3 w-3" /> Opened {formatDate(q.last_viewed_at)}
      </span>
    );
  }
  if (q.last_emailed_at || q.sent_at)
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] text-[#0088CC] whitespace-nowrap"
        data-testid={`quote-sent-stamp-${q.id}`}
      >
        <Mail className="h-3 w-3" /> Sent {formatDate(q.last_emailed_at || q.sent_at)}
      </span>
    );
  return null;
}

// Quotes built as vessel-option comparisons show each option's price
// (alternatives, never summed); everything else shows the line-item total.
function QuoteFigures({ q }) {
  const opts = (q.vessel_options || []).filter((o) => o.price != null && o.price !== "");
  if (opts.length > 0) {
    return (
      <div className="text-right space-y-0.5 max-w-[240px] ml-auto" data-testid={`quote-options-pricing-${q.id}`}>
        {opts.map((o, i) => (
          <div key={o.id || i} className="text-xs text-[#011836] tabular-nums leading-tight flex items-baseline justify-end gap-1.5">
            <span className="text-[#5B6B7C] truncate max-w-[130px]" title={o.label || `Option ${i + 1}`}>
              {o.label || `Option ${i + 1}`}
            </span>
            <span className="whitespace-nowrap">{formatMoney(o.price, q.currency)}</span>
          </div>
        ))}
      </div>
    );
  }
  return <>{formatMoney(q.total, q.currency)}</>;
}

export default function Quotes() {
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  // Support deep links like /quotes?status=sent from the dashboard
  // status-breakdown tiles.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStatus = searchParams.get("status");
  const [status, setStatus] = useState(
    ["draft", "sent", "accepted", "rejected"].includes(urlStatus) ? urlStatus : "all"
  );
  const changeStatus = (s) => {
    setStatus(s);
    setSearchParams(s === "all" ? {} : { status: s }, { replace: true });
  };
  // "Only mine" filter — surfaces only the quotes assigned to the
  // currently logged-in user. Helps a busy estimator focus without
  // fiddling with a name filter every time they open the page.
  const [mineOnly, setMineOnly] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .get("/quotes")
      .then(({ data }) => {
        if (!active) return;
        setQuotes(data);
        cachePut("quotes", data);
      })
      .catch(async (e) => {
        if (!e?.response) {
          const cached = await cacheGet("quotes");
          if (cached && active) {
            setQuotes(cached);
            toast.message("Offline — showing your saved quotes");
          } else if (active) {
            toast.message("Offline — quotes haven't been saved on this device yet");
          }
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const myId = user?.id;
    return quotes.filter((row) => {
      if (status !== "all" && row.status !== status) return false;
      if (mineOnly && row.assigned_to !== myId) return false;
      if (!q) return true;
      return (
        (row.title || "").toLowerCase().includes(q) ||
        (row.number || "").toLowerCase().includes(q) ||
        (row.client?.name || "").toLowerCase().includes(q) ||
        (row.client?.company || "").toLowerCase().includes(q) ||
        (row.assignee?.name || "").toLowerCase().includes(q)
      );
    });
  }, [quotes, query, status, mineOnly, user?.id]);

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-[1400px] mx-auto pb-24">
      <ViewOnlyBanner />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#0088CC] mb-2">
            Workspace
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#011836]">
            Quotes
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Create, edit, and manage shipbuilding quotes from draft to acceptance.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              try {
                await downloadApiFile("/quotes/export/summary.xlsx", "SOS-quotes-summary.xlsx");
                toast.success("Summary spreadsheet downloaded");
              } catch {
                toast.error("Export failed");
              }
            }}
            className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
            data-testid="export-all-quotes-btn"
            title="Download every quote as a one-row-per-quote Excel summary"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Export all
          </Button>
          <Link
            to="/quotes/new"
            data-testid="new-quote-btn"
            className={`inline-flex items-center gap-2 px-4 py-2.5 bg-[#0088CC] hover:bg-[#006DA6] text-white rounded-md font-display tracking-wide text-sm transition-colors ${
              canEdit ? "" : "hidden"
            }`}
          >
            <Plus className="h-4 w-4" /> New Quote
          </Link>
        </div>
      </div>

      <div className="sos-surface rounded-2xl">
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-[#DDE5EC]">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5B6B7C]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by quote #, title, or client…"
              className="pl-9 h-11 sm:h-10 bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
              data-testid="quotes-search-input"
            />
          </div>
          <Select value={status} onValueChange={changeStatus}>
            <SelectTrigger
              className="w-full sm:w-48 h-11 sm:h-10 bg-white border-[#DDE5EC] text-[#011836]"
              data-testid="quotes-status-filter"
            >
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
              {STATUS_FILTERS.map((s) => (
                <SelectItem key={s} value={s} data-testid={`filter-${s}`}>
                  {s === "all" ? "All statuses" : STATUS_META[s].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={() => setMineOnly((m) => !m)}
            className={`inline-flex items-center justify-center gap-1.5 px-4 h-11 sm:h-10 rounded-md border text-sm font-display font-semibold transition-colors ${
              mineOnly
                ? "bg-[#0088CC] border-[#0088CC] text-white"
                : "bg-white border-[#DDE5EC] text-[#5B6B7C] hover:text-[#011836] hover:border-[#C4D0DB]"
            }`}
            data-testid="quotes-mine-only-toggle"
            title={mineOnly ? "Show all quotes" : "Show only quotes assigned to me"}
          >
            <User className="h-3.5 w-3.5" />
            Only mine
          </button>
        </div>

        {/* Mobile card list — surfaces the same data as the table below
            without horizontal-scrolling on narrow screens. Tap anywhere
            on the card to open the quote. */}
        <div className="md:hidden divide-y divide-[#DDE5EC]">
          {loading && (
            <div className="px-4 py-10 text-center text-sm text-[#5B6B7C]">Loading…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="px-4 py-12 text-center">
              <FileText className="h-8 w-8 text-[#B6C2CD] mx-auto mb-3" />
              <div className="text-sm text-[#5B6B7C]">
                {quotes.length === 0
                  ? "No quotes yet — create your first one."
                  : "No quotes match your filters."}
              </div>
            </div>
          )}
          {filtered.map((q) => (
            <Link
              key={q.id}
              to={`/quotes/${q.id}`}
              className="block px-4 py-4 active:bg-white transition-colors"
              data-testid={`quote-card-${q.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-display text-sm font-semibold text-[#0088CC]">
                      {q.number}
                    </span>
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_META[q.status]?.className || ""}`}
                    >
                      {STATUS_META[q.status]?.label || q.status}
                    </span>
                  </div>
                  <div className="text-sm text-[#011836] font-display font-semibold truncate">
                    {q.title}
                  </div>
                  <div className="text-xs text-[#5B6B7C] mt-1 truncate">
                    {q.client?.name || "—"}
                    {q.client?.company && q.client.company !== q.client?.name ? ` · ${q.client.company}` : ""}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
                        q.assignee
                          ? "bg-[#0088CC]/15 text-[#0088CC] border border-[#0088CC]/30"
                          : "bg-amber-500/15 text-amber-700 border border-amber-400/30"
                      }`}
                      data-testid={`quote-card-assignee-${q.id}`}
                    >
                      {q.assignee ? q.assignee.name : "Unassigned"}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-[#64748B]">
                      {formatDate(q.created_at)}
                    </span>
                    <TrackingStamp q={q} />
                  </div>
                </div>
                <div className="font-display text-base font-bold text-[#011836] tabular-nums shrink-0">
                  <QuoteFigures q={q} />
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.25em] text-[#5B6B7C] border-b border-[#DDE5EC]">
                <th className="text-left px-4 py-3 whitespace-nowrap">Quote #</th>
                <th className="text-left px-4 py-3 w-1/4">Title</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Client</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Created</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Total</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-[#5B6B7C]">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <FileText className="h-8 w-8 text-[#B6C2CD] mx-auto mb-3" />
                    <div className="text-sm text-[#5B6B7C]">
                      {quotes.length === 0
                        ? "No quotes yet — create your first one."
                        : "No quotes match your filters."}
                    </div>
                  </td>
                </tr>
              )}
              {filtered.map((q) => (
                <tr
                  key={q.id}
                  className="border-b border-[#DDE5EC] hover:bg-white transition-colors"
                  data-testid={`quote-row-${q.id}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Link
                      to={`/quotes/${q.id}`}
                      className="font-display text-sm font-semibold text-[#0088CC] hover:underline"
                    >
                      {q.number}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#011836] max-w-[240px] truncate">{q.title}</td>
                  <td className="px-4 py-3 text-sm text-[#5B6B7C] hidden md:table-cell">
                    {q.client?.name || "—"}
                    {q.client?.company && q.client.company !== q.client?.name ? ` · ${q.client.company}` : ""}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#5B6B7C] hidden lg:table-cell">
                    {formatDate(q.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className={`text-xs px-2 py-0.5 rounded self-start ${STATUS_META[q.status]?.className || ""}`}>
                        {STATUS_META[q.status]?.label || q.status}
                      </span>
                      <TrackingStamp q={q} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-display font-semibold text-[#011836] tabular-nums">
                    <QuoteFigures q={q} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
